/**
 * Dashboard Stats Hook (New Backend)
 * Implements `DASHBOARD_DOCUMENTATION.md`
 *
 * Notes:
 * - No fallback/mock/sample data.
 * - Errors are surfaced to the UI.
 */

import { useQuery } from '@tanstack/react-query'
import { useOrganization } from '@/contexts/OrganizationContext'
import {
  fetchDashboardData,
  fetchWeeklyCompletedServices,
  fetchWeeklyPerformance,
} from '@/api/dashboard'
import type { DashboardStats } from '@/types/dashboard'

export const dashboardKeys = {
  all: ['dashboard'] as const,
  data: (organizationId: string) => [...dashboardKeys.all, 'data', organizationId] as const,
  weekly: (organizationId: string) => [...dashboardKeys.all, 'weekly', organizationId] as const,
  performance: (organizationId: string) =>
    [...dashboardKeys.all, 'performance', organizationId] as const,
}

export function useDashboardStats() {
  const { currentOrganization } = useOrganization()

  const query = useQuery<DashboardStats>({
    queryKey: dashboardKeys.data(currentOrganization?.id || ''),
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }
      return fetchDashboardData(currentOrganization.id)
    },
    enabled: !!currentOrganization?.id,
    staleTime: 1000 * 30, // 30s: dashboard should feel live
    refetchOnWindowFocus: true,
    retry: 1,
  })

  return {
    stats: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  }
}

/**
 * "Services Completed This Week" chart data.
 *
 * Split from `useDashboardStats` so it can cache with a long TTL (15 min)
 * independent of the live dashboard. Its query key is nested under
 * `dashboardKeys.all`, so the existing `invalidateAll()` in `useServiceActions`
 * still refetches it the moment a service is completed/changed.
 */
export function useWeeklyServices() {
  const { currentOrganization } = useOrganization()

  const query = useQuery({
    queryKey: dashboardKeys.weekly(currentOrganization?.id || ''),
    queryFn: () => fetchWeeklyCompletedServices(currentOrganization!.id),
    enabled: !!currentOrganization?.id,
    staleTime: 1000 * 60 * 15, // 15 min: retrospective aggregate, rarely changes
    refetchOnWindowFocus: false,
    retry: 1,
  })

  return {
    weeklyServices: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
  }
}

/**
 * Week-over-week performance bundle (comparison chart, KPI strip, status donut,
 * top breakdowns). Cached with the same long TTL as `useWeeklyServices` and, being
 * nested under `dashboardKeys.all`, still refetched by `invalidateAll()` on service actions.
 */
export function useWeeklyPerformance() {
  const { currentOrganization } = useOrganization()

  const query = useQuery({
    queryKey: dashboardKeys.performance(currentOrganization?.id || ''),
    queryFn: () => fetchWeeklyPerformance(currentOrganization!.id),
    enabled: !!currentOrganization?.id,
    staleTime: 1000 * 60 * 15, // 15 min: retrospective aggregate, rarely changes
    refetchOnWindowFocus: false,
    retry: 1,
  })

  return {
    performance: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
  }
}
